import React from 'react';
import { formatNumberWithCommas } from '../../../../../helpers/numericHelpers.js';
import { renderSensitiveCellValue } from '../../common/sensitiveFields.jsx';

export const shimmerKeyframes = `
  @keyframes shimmer {
    0%   { background-position: 200% 0 }
    100% { background-position: -200% 0 }
  }
`;

export const MONTH_NAMES = {
  Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April',
  May: 'May', Jun: 'June', Jul: 'July', Aug: 'August',
  Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December',
};

export const parseMonth = (month) => {
  if (!month) return null;
  const parts = month.split('-');
  if (parts.length !== 2) return null;
  const fullMonth = MONTH_NAMES[parts[0]] || parts[0];
  const year = parseInt(parts[1], 10) >= 50 ? `19${parts[1]}` : `20${parts[1]}`;
  return { abbr: parts[0], fullMonth, year };
};

export const ordinalSuffix = (day) => {
  if (day === 1 || day === 21 || day === 31) return 'st';
  if (day === 2 || day === 22) return 'nd';
  if (day === 3 || day === 23) return 'rd';
  return 'th';
};

/**
 * Returns the label for the default date radio option.
 * Current month: "Today (10th April)"
 * Past month:    "Last Date (31st March)"
 */
export const currentDateLabel = (month) => {
  const parsed = parseMonth(month);
  if (!parsed) return 'Today';
  const yearNum = parseInt(parsed.year, 10);
  const monthIdx = Object.keys(MONTH_NAMES).indexOf(parsed.abbr);
  if (monthIdx === -1) return 'Today';
  const now = new Date();
  const isPastMonth = yearNum < now.getFullYear() || (yearNum === now.getFullYear() && monthIdx < now.getMonth());
  if (isPastMonth) {
    const lastDay = new Date(yearNum, monthIdx + 1, 0).getDate();
    return `Last Date (${lastDay}${ordinalSuffix(lastDay)} ${parsed.fullMonth})`;
  }
  const today = now.getDate();
  return `Today (${today}${ordinalSuffix(today)} ${parsed.fullMonth})`;
};

/** Matches the backend's "MMM-YY" month-column keys, e.g. "Jan-26". */
export const MONTH_KEY_RE = /^[A-Z][a-z]{2}-\d{2}$/;

/**
 * @param showZeroKeys Set of column keys where a real 0 must render as "0"
 *   rather than the usual em-dash. The AO register needs this: a month the AO
 *   billed nothing in is meaningful data, and must not look like a month before
 *   the AO joined the register (which is genuinely absent and renders "—").
 */
export const buildInsightColumns = (headers, columnSums, totalsLoading, showZeroKeys = null) =>
  headers.map((h) => {
    // A header carrying sub_headers is a banner spanning its children (e.g.
    // "Actual Sales" over the month grid). The table wants those as a nested
    // `columns` array; the banner itself renders no cells of its own.
    if (h.sub_headers?.length) {
      return {
        key: h.key,
        id: h.key,
        Header: h.label,
        columns: buildInsightColumns(h.sub_headers, columnSums, totalsLoading, showZeroKeys),
      };
    }

    const isNumeric = h.data_type === 'number';
    const zeroIsData = !!showZeroKeys?.has(h.key);

    const headerFn = () => (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.3' }}>
        {(h.label || '').split('\n').map((line, i) => <span key={i}>{line}</span>)}
        {(isNumeric || h.key === 'dealer_code' || h.key === 'account_owner') && (
          totalsLoading ? (
            <span style={{
              display: 'block',
              width: 60,
              height: 10,
              borderRadius: 3,
              marginTop: 4,
              background: 'linear-gradient(90deg, rgba(255,255,255,0.2) 25%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.2) 75%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
            }} />
          ) : columnSums[h.key] ? (
            <span style={{ 
              fontWeight: 600,
              fontSize: "12px",
              opacity: 1,
              marginTop: "2px",
              color: "#ffffff",
              background: "#000000",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              padding: "2px 10px",
              borderRadius: "10px" }}>
              {columnSums[h.key]}
            </span>
          ) : null
        )}
      </span>
    );

    const defaultTextCell = ({ row }) => {
      const val = row.original[h.key];
      return (
        <span>{renderSensitiveCellValue(h.key, val, { empty: '\u2014' })}</span>
      );
    };

    return {
      key: h.key,
      id: h.key,
      accessor: h.key,
      label: h.label,
      Header: headerFn,
      data_type: h.data_type,
      sortable: h.sortable ?? false,
      filterable: h.filterable ?? false,
      width: h.key === 'dealer' ? 220 : h.key === 'account_owner' ? 200 : h.key === 'dealer_name' ? 200 : h.key === 'dealer_code' ? 120 : h.key === 'zone' ? 100 : h.key === 'region' ? 120 : h.key === 'district' ? 120 : 120,
      Cell: isNumeric
        ? ({ row }) => {
            const val = row.original[h.key];
            const isBlank = val === undefined || val === null || val === '';
            if (isBlank || (val === 0 && !zeroIsData)) {
              return <span className='flex jc-center' style={{ color: '#aaa' }}>&mdash;</span>;
            }
            return <span className='flex jc-center'>{formatNumberWithCommas(Math.round(val * 100) / 100)}</span>;
          }
        : defaultTextCell,
    };
  });
